import { CoreEvent } from '@carvana/core-events';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ModalContext from '|/app/modals';
import { Dashboard, Summary } from '|/app/services';
import routeNameComponentLookupImport from '|/app/purchase/flow/step-registry';
import {
  VERIFX_NAVIGATION_BLOCK_CHANGED,
  VERIFX_OPEN_CANCEL_ORDER_MODAL,
  VERIFX_OPEN_EDIT_ORDER_MODAL,
  coreEventOffWithType,
  type VerifxBlockingEvents,
  type VerifxModalEvents
} from '|/app/verifx-events';
import { SummaryContext } from '|/context';
import StepUrlsContext from '|/context/step-urls-context';

type RouteConfig = Record<
  string,
  {
    completionTimeEstimate?: number;
    [key: string]: unknown;
  }
>;

const routeNameComponentLookup = routeNameComponentLookupImport as RouteConfig;

export interface NavigationRailVmProps {
  dashboard: Dashboard | null;
  summary: Summary | null;
  onNavigate?: () => void;
}

export const useNavigationRailVm = ({
  dashboard = null,
  summary = null,
  onNavigate = () => undefined
}: NavigationRailVmProps) => {
  const { steps = [], sections } = dashboard ?? {};
  const { vehicle = null, transfer: { deliveryType = null } = {} } = summary ?? {};
  const { pathname } = useLocation();
  const orderedSteps = steps.sort((a, b) => a.order - b.order);
  const latestStep = orderedSteps.find(step => step.isEnabled && !step.isComplete);

  // Just use what backend provides, no fallbacks or defaults
  const actualSections = sections ?? [];

  const currentSection = latestStep?.section;
  const { stepUrlMap } = useContext(StepUrlsContext);

  const segments = pathname.split('/');
  const currentRouteSegment = segments[segments.indexOf('steps') + 1];
  const viewedStep = steps.find(step => (stepUrlMap[step.id] ?? step.url) === currentRouteSegment);
  const viewedSection = viewedStep?.section;

  const sectionsArray = actualSections
    .filter(section => section.sectionName !== 'Dashboard')
    .map(section => ({
      sectionNumber: section.sectionId,
      sectionName: section.sectionName,
      steps: steps
        .filter(
          step =>
            step.section === section.sectionId &&
            step.isEnabled &&
            (section.sectionId === currentSection ||
              section.sectionId === viewedSection ||
              step.isComplete)
        )
        .sort((a, b) => a.order - b.order)
    }))
    .sort((a, b) => a.sectionNumber - b.sectionNumber);

  const [, , , route] = window.location.pathname.split('/');

  const {
    setEditOrderModalDSOpen,
    setCancelOrderModalDSOpen,
    setKiqExitWarningModalNavRailOpen,
    isCancelOrderModalShown,
    isCancelOrderModalDSShown,
    isEditOrderModalDSShown,
    isKiqExitWarningModalNavRailShown
  } = useContext(ModalContext);

  const [isMfeBlocking, setIsMfeBlocking] = useState(false);

  useEffect(() => {
    const handler = (event: { shouldBlock: boolean }) => {
      setIsMfeBlocking(event.shouldBlock);
    };
    CoreEvent.onWithType<VerifxBlockingEvents, typeof VERIFX_NAVIGATION_BLOCK_CHANGED>(
      VERIFX_NAVIGATION_BLOCK_CHANGED,
      handler
    );
    return () => {
      coreEventOffWithType<VerifxBlockingEvents, typeof VERIFX_NAVIGATION_BLOCK_CHANGED>(
        VERIFX_NAVIGATION_BLOCK_CHANGED,
        handler
      );
    };
  }, []);

  const [editOrCancel, setEditOrCancel] = useState('');
  const [editingSectionName, setEditingSectionName] = useState<string>('');
  const [targetStep, setTargetStep] = useState<string | undefined>();
  const { setShowSummary } = useContext(SummaryContext);

  useEffect(() => {
    const handler = () => {
      setCancelOrderModalDSOpen(true);
    };
    CoreEvent.onWithType<VerifxModalEvents, typeof VERIFX_OPEN_CANCEL_ORDER_MODAL>(
      VERIFX_OPEN_CANCEL_ORDER_MODAL,
      handler
    );
    return () => {
      coreEventOffWithType<VerifxModalEvents, typeof VERIFX_OPEN_CANCEL_ORDER_MODAL>(
        VERIFX_OPEN_CANCEL_ORDER_MODAL,
        handler
      );
    };
  }, [setCancelOrderModalDSOpen]);

  const openEditOrderModal = useCallback(
    ({
      editingSectionName = '',
      targetStep: requestedTargetStep
    }: VerifxModalEvents['VERIFX_OPEN_EDIT_ORDER_MODAL']) => {
      onNavigate();
      setShowSummary(false);
      setEditingSectionName(editingSectionName);
      setTargetStep(requestedTargetStep);

      if (isMfeBlocking) {
        setEditOrCancel('edit');
        setKiqExitWarningModalNavRailOpen(true);
      } else {
        setEditOrderModalDSOpen(true);
      }
    },
    [
      isMfeBlocking,
      onNavigate,
      setEditOrderModalDSOpen,
      setKiqExitWarningModalNavRailOpen,
      setShowSummary
    ]
  );

  useEffect(() => {
    const handler = (event: VerifxModalEvents['VERIFX_OPEN_EDIT_ORDER_MODAL']) => {
      openEditOrderModal(event);
    };
    CoreEvent.onWithType<VerifxModalEvents, typeof VERIFX_OPEN_EDIT_ORDER_MODAL>(
      VERIFX_OPEN_EDIT_ORDER_MODAL,
      handler
    );
    return () => {
      coreEventOffWithType<VerifxModalEvents, typeof VERIFX_OPEN_EDIT_ORDER_MODAL>(
        VERIFX_OPEN_EDIT_ORDER_MODAL,
        handler
      );
    };
  }, [openEditOrderModal]);

  const scrollToTop = useCallback(() => {
    if (window.scrollY !== 0) {
      window.scrollTo(0, 0);
    }
  }, []);

  const getSectionStatusMappings = (sectionName: string): Record<number, string | undefined> => {
    const currentSection = latestStep?.section ?? 1;
    const currentSectionName = latestStep?.sectionName ?? '';

    if (sectionName === currentSectionName) {
      return { [currentSection]: 'In progress' };
    }

    return {};
  };

  const handleEditClick = (sectionName?: string) => {
    openEditOrderModal({ editingSectionName: sectionName ?? '' });
  };

  const handleCancelClick = () => {
    onNavigate();
    setShowSummary(false);
    if (isMfeBlocking) {
      setEditOrCancel('cancel');
      setKiqExitWarningModalNavRailOpen(true);
    } else {
      setCancelOrderModalDSOpen(true);
    }
  };

  const handleStepClick = (isDisabled: boolean, _stepUrl: string) => {
    if (isDisabled) {
      return false;
    }
    scrollToTop();
    onNavigate();
    setShowSummary(false);
    return true;
  };

  const handleModalClose = (modalType: 'edit' | 'cancel' | 'kiq') => {
    if (modalType === 'edit') {
      setEditOrderModalDSOpen(false);
      setTargetStep(undefined);
    } else if (modalType === 'cancel') {
      setCancelOrderModalDSOpen(false);
    } else if (modalType === 'kiq') {
      setKiqExitWarningModalNavRailOpen(false);
    }
  };

  const handleKiqLeave = () => {
    setKiqExitWarningModalNavRailOpen(false);
    if (editOrCancel === 'cancel') {
      setCancelOrderModalDSOpen(true);
    }
    if (editOrCancel === 'edit') {
      setEditOrderModalDSOpen(true);
    }
  };

  return {
    vehicle,
    deliveryType,
    orderedSteps,
    latestStep,
    sectionsArray,
    route,
    pathname,
    stepUrlMap,
    isCancelOrderModalDSShown: isCancelOrderModalDSShown ?? isCancelOrderModalShown,
    isEditOrderModalDSShown,
    isKiqExitWarningModalNavRailShown,
    editOrCancel,
    editingSectionName,
    targetStep,
    getSectionStatusMappings,
    handleEditClick,
    handleCancelClick,
    handleStepClick,
    handleModalClose,
    handleKiqLeave,
    scrollToTop,
    routeNameComponentLookup,
    currentSection,
    viewedSection
  };
};
